const STORAGE_PREFIX = 'toro:checkout';

const TTL = 30 * 60 * 1000;

/**
 * Allow-list: só estes campos são persistidos. Com deny-list, qualquer campo
 * novo em `initialState` passaria a ser gravado por padrão — inclusive um
 * sensível.
 *
 * Ficam de fora, deliberadamente: dados de cartão e validade; aceites de
 * contrato, que precisam ser reconfirmados a cada sessão; e o cupom, porque o
 * desconto é revalidado no servidor e um cupom restaurado sem revalidação
 * mostraria preço divergente do que será cobrado.
 */
const PERSISTED_FIELDS = [
  'full_name',
  'email',
  'phone_number',
  'birth_date',
  'document_number',
  'instituicao_financeira',
  'another_financial_instituition',
  'city',
  'state',
  'street',
  'number',
  'district',
  'post_code',
  'complement',
  'installments',
  'type',
  'promo_opt_in',
  'selectedModules',
  'ibge',
];

export function buildStorageKey(curso, idTurma) {
  return [STORAGE_PREFIX, curso || '', idTurma || ''].join(':');
}

export function saveCheckoutState(key, values, step, addressLock) {
  if (!key || !values) return;

  const persistable = PERSISTED_FIELDS.reduce((acc, field) => {
    if (values[field] !== undefined) acc[field] = values[field];

    return acc;
  }, {});

  try {
    // sessionStorage e não localStorage: os dados sobrevivem ao reload, que é
    // o caso de uso, mas não ficam na máquina depois de fechar a aba.
    window.sessionStorage.setItem(
      key,
      JSON.stringify({
        values: persistable,
        step: step || 1,
        addressLock: addressLock || null,
        expiry: Date.now() + TTL,
      })
    );
  } catch (e) {
    // Storage cheio ou indisponível: seguir sem persistir.
  }
}

export function loadCheckoutState(key) {
  if (!key) return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const stored = JSON.parse(raw);

    if (!stored || !stored.values || Date.now() > stored.expiry) {
      clearCheckoutState(key);
      return null;
    }

    // Registro gravado por uma versão anterior pode ter campos fora da lista.
    stored.values = PERSISTED_FIELDS.reduce((acc, field) => {
      if (stored.values[field] !== undefined) acc[field] = stored.values[field];

      return acc;
    }, {});

    return stored;
  } catch (e) {
    clearCheckoutState(key);
    return null;
  }
}

export function buildPixStorageKey(curso, idTurma) {
  return [STORAGE_PREFIX, 'pix', curso || '', idTurma || ''].join(':');
}

/**
 * O Pix em andamento fica em sessionStorage: o QR Code não deve sobreviver ao
 * fechamento da aba, mas precisa sobreviver a um F5.
 */
export function savePixState(key, pixState) {
  if (!key || !pixState) return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(pixState));
  } catch (e) {
    // Sem sessionStorage o Pix simplesmente não é reidratado.
  }
}

export function loadPixState(key) {
  if (!key) return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const stored = JSON.parse(raw);
    const deadline = stored?.callback?.deadline;

    if (!stored?.callback?.pixChave || !deadline) return null;

    if (deadline <= Date.now()) {
      clearPixState(key);
      return null;
    }

    return stored;
  } catch (e) {
    clearPixState(key);
    return null;
  }
}

export function clearPixState(key) {
  if (!key) return;

  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // Nada a fazer.
  }
}

export function clearCheckoutState(key) {
  if (!key) return;

  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // Nada a fazer: o TTL cobre o registro remanescente.
  }
}
